import { Link } from 'react-router-dom';
import ScrollReveal from '@/components/ScrollReveal';
import Seo from '@/components/Seo';
import { buildBreadcrumbSchema } from '@/lib/seo-schemas';
import { Check, FileText, Star } from 'lucide-react';

// Manchester landing page for "inventory clerk Manchester" / "inventory services Manchester" /
// "property inventory Manchester" (SEO / AEO page). Facts confirmed by Victor 02/11/2026:
// same prices as London, covers all of Greater Manchester, 4-hour express available.
// Prices are inc VAT and match lib/pricing.js (ex VAT x 1.2).

const SITE_URL = 'https://daleyinventories.com';
const PAGE_PATH = '/property-inventory-manchester';
const PAGE_URL = `${SITE_URL}${PAGE_PATH}`;
const SAMPLE_REPORT_URL = 'https://storage.googleapis.com/daley-inventories-assets/sample-report.pdf';
const LOGO_URL = 'https://storage.googleapis.com/daley-inventories-assets/daley_logo_black%20writing.png';
// Address exactly as on the Google Business Profile: 30 Churchill Place, Canary Wharf, London, E14 5EU
const ADDRESS = {
  '@type': 'PostalAddress',
  streetAddress: '30 Churchill Place, Canary Wharf',
  addressLocality: 'London',
  postalCode: 'E14 5EU',
  addressCountry: 'GB',
};

const AREAS = ['Manchester', 'Salford', 'Trafford', 'Stockport', 'Bolton', 'Oldham', 'Bury', 'Rochdale', 'Tameside', 'Wigan'];

const PRICES = [
  { size: 'Studio', inventory: 120, checkIn: 120, checkOut: 108 },
  { size: '1 bedroom', inventory: 132, checkIn: 144, checkOut: 132 },
  { size: '2 bedrooms', inventory: 144, checkIn: 156, checkOut: 144 },
  { size: '3 bedrooms', inventory: 168, checkIn: 180, checkOut: 168 },
  { size: '4 bedrooms', inventory: 180, checkIn: 192, checkOut: 180 },
  { size: '5 bedrooms', inventory: 204, checkIn: 216, checkOut: 204 },
];

const ADD_ONS = [
  '4-hour express report: £60',
  'Furnished property, or each extra room (en-suite, WC, utility): £12',
  'Sunday or bank holiday appointment: £30',
];

const INCLUDED = [
  'A room-by-room record of condition and contents',
  'Date-stamped photos throughout',
  'Meter readings',
  'Smoke and carbon monoxide alarm check',
  'Report delivered within 24–48 hours',
];

const WHY = [
  { strong: '10 years', rest: ' in business' },
  { strong: '20,000 landlords', rest: ' served' },
  { strong: '4-hour express reports', rest: ': when you need the report the same day' },
  { strong: 'One company for everything:', rest: ' inventories, EPCs, EICRs, gas safety certificates and end-of-tenancy cleaning' },
  {
    strong: 'Beat any price guarantee:',
    rest: " send us a written quote from another inventory company for the same service and property size, and we'll beat it by £5. The quote must be dated within the last 14 days, come from an established, insured inventory company, and not be a special offer or first-time discount.",
  },
];

const FAQS = [
  {
    q: 'How much does a property inventory cost in Manchester?',
    a: 'At Daley Inventories, a property inventory in Manchester starts at £120 including VAT for a studio, and a check-out starts at £108. The price depends on the number of bedrooms, bathrooms and extra rooms, and on whether the property is furnished.',
  },
  {
    q: 'Do you cover Salford, Stockport and the rest of Greater Manchester?',
    a: 'Yes. Daley Inventories covers all of Greater Manchester: Manchester, Salford, Trafford, Stockport, Bolton, Oldham, Bury, Rochdale, Tameside and Wigan.',
  },
  {
    q: 'How quickly will I get the report?',
    a: 'Daley Inventories delivers reports within 24–48 hours. If you need it faster, the 4-hour express service costs £60.',
  },
  {
    q: 'How long does an inventory take?',
    a: 'A Daley Inventories clerk usually spends about an hour on site for a studio or one-bedroom flat. Larger and furnished homes take longer.',
  },
  {
    q: "What's the difference between an inventory, a check-in and a check-out?",
    a: "An inventory records the property's condition and contents before the tenancy. A check-in confirms that record with the tenant on move-in day. A check-out compares the property at the end of the tenancy against the original report, which is what deposit decisions rely on.",
  },
  {
    q: 'Do I need to be there?',
    a: "No. Our clerk can collect the keys from you or your letting agent, so you don't need to attend.",
  },
  {
    q: 'Do you work with letting agents in Manchester?',
    a: 'Yes. Letting agents get 10% off inventories, check-ins and check-outs, and new agencies get their first report free on properties up to 3 bedrooms.',
  },
  {
    q: 'Do you work on weekends?',
    a: 'Yes. Sunday and bank holiday appointments carry a £30 surcharge.',
  },
];

const gbp = (n) => `£${n}`;

const AREA_SERVED = [
  { '@type': 'AdministrativeArea', name: 'Greater Manchester' },
  ...AREAS.map((name) => ({ '@type': 'City', name })),
];

function offer(name, low, high) {
  return {
    '@type': 'Offer',
    name,
    priceCurrency: 'GBP',
    price: low.toFixed(2),
    priceSpecification: {
      '@type': 'PriceSpecification',
      minPrice: low.toFixed(2),
      maxPrice: high.toFixed(2),
      priceCurrency: 'GBP',
      valueAddedTaxIncluded: true,
    },
    areaServed: { '@type': 'AdministrativeArea', name: 'Greater Manchester' },
    url: PAGE_URL,
  };
}

function buildSchemas() {
  const organization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}#organization`,
    name: 'Daley Inventories Ltd',
    url: SITE_URL,
    image: LOGO_URL,
    logo: LOGO_URL,
    telephone: '+442089355492',
    email: 'info@daleyinventories.com',
    address: ADDRESS,
  };

  const business = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': SITE_URL,
    name: 'Daley Inventories Ltd',
    url: SITE_URL,
    telephone: '+442089355492',
    email: 'info@daleyinventories.com',
    foundingDate: '2016',
    priceRange: '££',
    image: LOGO_URL,
    logo: LOGO_URL,
    address: ADDRESS,
    parentOrganization: { '@id': `${SITE_URL}#organization` },
    areaServed: [
      { '@type': 'City', name: 'London' },
      { '@type': 'AdministrativeArea', name: 'Greater Manchester' },
    ],
    openingHoursSpecification: [{
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '09:00',
      closes: '18:00',
    }],
  };

  const service = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${PAGE_URL}#service`,
    name: 'Inventory clerk services in Greater Manchester',
    serviceType: 'Property inventory',
    description: 'Property inventories, check-ins and check-outs across Greater Manchester from £120 including VAT. Reports delivered within 24–48 hours, with a 4-hour express option for £60.',
    url: PAGE_URL,
    provider: { '@id': SITE_URL },
    areaServed: AREA_SERVED,
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Manchester inventory prices (including VAT)',
      itemListElement: [
        offer('Property inventory', 120, 204),
        offer('Check-in report', 120, 216),
        offer('Check-out report', 108, 204),
      ],
    },
  };

  const faq = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  const breadcrumb = buildBreadcrumbSchema(PAGE_PATH, 'Property Inventory Manchester');

  return [breadcrumb, organization, business, service, faq];
}

const CONTAINER = 'max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16';
const H2 = 'font-display font-extrabold text-navy mb-6';
const H2_SIZE = { fontSize: 'clamp(1.75rem, 3vw, 2.25rem)' };
const BTN_ORANGE = 'inline-flex items-center justify-center px-8 py-3 bg-orange text-navy font-semibold rounded-full hover:bg-orange-dark transition-colors';
const BTN_NAVY = 'inline-flex items-center justify-center px-8 py-3 bg-navy text-white font-semibold rounded-full hover:bg-navy/90 transition-colors';
const LINK = 'text-navy font-semibold underline hover:text-orange transition-colors';

export default function PropertyInventoryManchester() {
  const schemas = buildSchemas();

  return (
    <div className="pt-20">
      <Seo
        title="Inventory Clerk Manchester from £120 inc VAT | Daley Inventories"
        description="Inventory clerk services across Greater Manchester from £120 inc VAT. Inventories, check-ins and check-outs, reports in 24–48 hours, 4-hour express."
        canonical={PAGE_URL}
        jsonLd={schemas}
      />

      {/* Hero: no animation, no image, so it paints straight away */}
      <section className="py-16 md:py-24">
        <div className={CONTAINER}>
          <div className="max-w-4xl">
            <p className="caps-label text-orange mb-4">Greater Manchester · Inventories · Check-ins · Check-outs</p>
            <h1 className="font-display font-extrabold text-navy mb-8" style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}>
              Inventory clerks in Manchester
            </h1>
            <p className="text-lg text-navy leading-relaxed mb-4">
              <strong>
                Daley Inventories provides inventory clerk services across Greater Manchester: property inventories, check-ins and check-outs from £120 including VAT. Reports are delivered within 24–48 hours, with a 4-hour express option for £60.
              </strong>{' '}
              We've been doing this for 10 years and have worked with 20,000 landlords.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Link to="/request-quote" className={BTN_ORANGE}>Get an instant quote</Link>
              <Link to="/book-now" className={BTN_NAVY}>Book now</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Prices */}
      <section className="pb-16 md:pb-24">
        <div className={CONTAINER}>
          <ScrollReveal>
            <h2 className={H2} style={H2_SIZE}>Prices (including VAT)</h2>
            <div className="overflow-x-auto bg-white rounded-2xl border border-navy/10">
              <table className="w-full text-left min-w-[520px]">
                <caption className="sr-only">Manchester property inventory, check-in and check-out prices including VAT</caption>
                <thead>
                  <tr className="bg-navy text-white">
                    <th scope="col" className="px-6 py-4 font-semibold">Property</th>
                    <th scope="col" className="px-6 py-4 font-semibold">Inventory</th>
                    <th scope="col" className="px-6 py-4 font-semibold">Check-in</th>
                    <th scope="col" className="px-6 py-4 font-semibold">Check-out</th>
                  </tr>
                </thead>
                <tbody>
                  {PRICES.map((row) => (
                    <tr key={row.size} className="border-t border-navy/10">
                      <th scope="row" className="px-6 py-4 font-semibold text-navy">{row.size}</th>
                      <td className="px-6 py-4 text-navy">{gbp(row.inventory)}</td>
                      <td className="px-6 py-4 text-navy">{gbp(row.checkIn)}</td>
                      <td className="px-6 py-4 text-navy">{gbp(row.checkOut)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="grid md:grid-cols-2 gap-8 mt-8">
              <div>
                <h3 className="font-bold text-navy text-lg mb-3">Add-ons</h3>
                <ul className="space-y-2">
                  {ADD_ONS.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-muted-foreground">
                      <Check className="text-orange flex-shrink-0 mt-1" size={18} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-sm text-muted-foreground self-end">
                Prices are based on the property details you give us and may change if the property differs.
              </p>
            </div>
            <p className="mt-8">
              <Link to="/letting-agent-inventories" className={LINK}>Inventory services for letting agents: 10% off, first report free</Link>
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* What's included + sample report */}
      <section className="pb-16 md:pb-24">
        <div className={CONTAINER}>
          <div className="grid lg:grid-cols-2 gap-12">
            <ScrollReveal>
              <h2 className={H2} style={H2_SIZE}>What's included</h2>
              <ul className="space-y-3">
                {INCLUDED.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-lg text-navy">
                    <Check className="text-orange flex-shrink-0 mt-1" size={20} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <div className="bg-navy text-white rounded-2xl p-8 lg:p-10 h-full flex flex-col justify-between gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-orange/20 flex items-center justify-center flex-shrink-0 rounded-xl">
                    <FileText className="text-orange" size={28} />
                  </div>
                  <p className="text-white/80 leading-relaxed">
                    See a real example: our 57-page sample check-out report, with photos of every room, meter readings and a clear list of actions.
                  </p>
                </div>
                <a
                  href={SAMPLE_REPORT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="self-start inline-flex items-center gap-2 px-6 py-3 bg-orange text-navy font-semibold rounded-full hover:bg-orange-dark transition-colors"
                >
                  View a sample report
                </a>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Why Daley */}
      <section className="pb-16 md:pb-24">
        <div className={CONTAINER}>
          <ScrollReveal>
            <h2 className={H2} style={H2_SIZE}>Why Manchester landlords choose Daley</h2>
            <ul className="grid md:grid-cols-2 gap-4">
              {WHY.map((item) => (
                <li key={item.strong} className="bg-white rounded-2xl border border-navy/10 p-6 flex items-start gap-3">
                  <Star className="text-orange flex-shrink-0 mt-1" size={18} />
                  <span className="text-muted-foreground">
                    <strong className="text-navy">{item.strong}</strong>{item.rest}
                  </span>
                </li>
              ))}
            </ul>
          </ScrollReveal>
        </div>
      </section>

      {/* Areas */}
      <section className="pb-16 md:pb-24">
        <div className={CONTAINER}>
          <ScrollReveal>
            <h2 className={H2} style={H2_SIZE}>Areas we cover</h2>
            <p className="text-lg text-muted-foreground max-w-3xl">
              Daley Inventories covers all of Greater Manchester: Manchester, Salford, Trafford, Stockport, Bolton, Oldham, Bury, Rochdale, Tameside and Wigan. We also cover{' '}
              <Link to="/property-inventory-london" className={LINK}>every London borough</Link>.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQs: plain HTML so answers are readable by search engines and AI assistants */}
      <section className="pb-16 md:pb-24">
        <div className={CONTAINER}>
          <ScrollReveal>
            <h2 className={H2} style={H2_SIZE}>Frequently asked questions</h2>
            <div className="space-y-4 max-w-4xl">
              {FAQS.map(({ q, a }) => (
                <div key={q} className="bg-white rounded-2xl border border-navy/10 p-6">
                  <h3 className="font-bold text-navy text-lg mb-2">{q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Ready to book */}
      <section className="py-16 md:py-24 bg-navy text-white">
        <div className={`${CONTAINER} text-center`}>
          <ScrollReveal>
            <h2 className="font-display font-extrabold mb-4" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)' }}>Ready to book in Manchester?</h2>
            <p className="text-white/70 mb-8 max-w-xl mx-auto">
              Get an instant quote online or call{' '}
              <a href="tel:02089355492" className="font-bold text-white underline">0208 935 5492</a>.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/request-quote" className={BTN_ORANGE}>Get an instant quote</Link>
              <Link to="/book-now" className="inline-flex items-center justify-center px-8 py-3 border border-white/40 text-white font-semibold rounded-full hover:bg-white/10 transition-colors">Book now</Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
